import { useEffect, useRef, useState } from 'react';
import { relogio } from '@/conversas/midiaEnvio';

/**
 * Gravador de áudio para a Central (PLANO 5.3).
 *
 * A recepção responde muita coisa mais rápido falando do que digitando, e o
 * paciente já manda áudio — a conversa ficava manca em um só sentido.
 *
 * O arquivo NÃO sai daqui: o componente devolve o `Blob` e quem chama decide o
 * que fazer. Isso mantém a subida ao Storage num lugar só (`Composer`).
 */

/**
 * Teto de 5 minutos.
 *
 * Não é o limite do WhatsApp — é o das `storage.rules`, chegando por outro
 * caminho: opus em ~24 kbps rende ~9 MB em cinco minutos, ainda longe dos 25 MB,
 * mas um gravador esquecido aberto encheria o bucket sem ninguém notar. Parar
 * sozinho preserva o que já foi dito, em vez de descartar.
 */
const LIMITE_SEGUNDOS = 5 * 60;

/**
 * Formato de gravação, na ordem de preferência.
 *
 * `audio/ogg;codecs=opus` é o que o WhatsApp usa nativamente. O Safari não
 * grava ogg e só oferece mp4 — daí a lista, e daí `isTypeSupported`: passar um
 * mime não suportado ao MediaRecorder lança na hora de começar.
 */
const FORMATOS = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

const formatoSuportado = () =>
  FORMATOS.find((f) => window.MediaRecorder?.isTypeSupported?.(f)) ?? '';

const MENSAGENS_MIC = {
  NotAllowedError:
    'Permissão do microfone negada. Libere o microfone para este site nas configurações do navegador e tente de novo.',
  NotFoundError: 'Nenhum microfone encontrado neste computador.',
  NotReadableError: 'O microfone está em uso por outro programa.',
  SecurityError: 'O navegador bloqueou o microfone. A página precisa estar em HTTPS.',
};

export default function GravarAudio({ aoConcluir, aoCancelar, ocupado }) {
  const [gravando, setGravando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState(null);

  const gravadorRef = useRef(null);
  const pedacosRef = useRef([]);
  const blobRef = useRef(null);

  /**
   * Solta microfone e blob ao desmontar.
   *
   * Sem parar as trilhas, a luz do microfone fica acesa depois de fechar o
   * gravador — o atendente pensa que ainda está sendo ouvido, e está certo.
   */
  useEffect(
    () => () => {
      gravadorRef.current?.stream?.getTracks().forEach((t) => t.stop());
      if (previa) URL.revokeObjectURL(previa);
    },
    [previa],
  );

  useEffect(() => {
    if (!gravando) return undefined;
    const id = setInterval(() => setSegundos((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [gravando]);

  // Corte por tempo: para o gravador, preservando o que já foi dito.
  useEffect(() => {
    if (gravando && segundos >= LIMITE_SEGUNDOS) parar();
  }, [gravando, segundos]);

  async function comecar() {
    setErro(null);
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setErro('Este navegador não grava áudio. Use o Chrome ou o Edge atualizado.');
      return;
    }

    let trilha;
    try {
      trilha = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.error('[OdontoSDR] Microfone indisponível:', e);
      setErro(MENSAGENS_MIC[e?.name] ?? 'Não foi possível acessar o microfone.');
      return;
    }

    const mimeType = formatoSuportado();
    const gravador = new MediaRecorder(trilha, mimeType ? { mimeType } : undefined);
    pedacosRef.current = [];

    gravador.ondataavailable = (e) => {
      if (e.data?.size > 0) pedacosRef.current.push(e.data);
    };
    gravador.onstop = () => {
      // O tipo do gravador, não o da lista: o navegador pode ter escolhido
      // outro, e um blob rotulado errado chega ao WhatsApp como arquivo
      // quebrado.
      const tipo = gravador.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(pedacosRef.current, { type: tipo });
      blobRef.current = blob;
      setPrevia(URL.createObjectURL(blob));
      trilha.getTracks().forEach((t) => t.stop());
    };

    gravadorRef.current = gravador;
    setSegundos(0);
    gravador.start();
    setGravando(true);
  }

  function parar() {
    if (gravadorRef.current?.state === 'recording') gravadorRef.current.stop();
    setGravando(false);
  }

  function descartar() {
    parar();
    if (previa) URL.revokeObjectURL(previa);
    blobRef.current = null;
    setPrevia(null);
    setSegundos(0);
    setErro(null);
    aoCancelar?.();
  }

  function enviar() {
    if (!blobRef.current) return;
    const extensao = blobRef.current.type.includes('ogg')
      ? 'ogg'
      : blobRef.current.type.includes('mp4')
        ? 'm4a'
        : 'webm';
    aoConcluir({
      arquivo: blobRef.current,
      nome: `audio-${Date.now()}.${extensao}`,
      tipo: 'audio',
      mimetype: blobRef.current.type,
      legenda: '',
    });
  }

  return (
    <div className="gravador">
      {erro && <p className="erro">{erro}</p>}

      {!gravando && !previa && (
        <div className="gravador-linha">
          <button type="button" className="btn-primario compacto" onClick={comecar}>
            ● Gravar
          </button>
          <button type="button" className="btn-texto" onClick={descartar}>
            Cancelar
          </button>
        </div>
      )}

      {gravando && (
        <div className="gravador-linha">
          <span className="gravador-pulso" aria-hidden="true" />
          <strong className="gravador-tempo" role="timer" aria-live="off">
            {relogio(segundos)}
          </strong>
          <span className="campo-ajuda">gravando… máximo {relogio(LIMITE_SEGUNDOS)}</span>
          <button type="button" className="btn-primario compacto" onClick={parar}>
            ■ Parar
          </button>
          <button type="button" className="btn-texto" onClick={descartar}>
            Descartar
          </button>
        </div>
      )}

      {previa && !gravando && (
        <div className="gravador-linha">
          {/* Ouvir antes de mandar: áudio gravado sem querer, ou mudo, é o
              erro mais comum — e não dá para desfazer depois de enviado. */}
          <audio className="msg-audio" controls src={previa} />
          <button
            type="button"
            className="btn-primario compacto"
            onClick={enviar}
            disabled={ocupado}
          >
            {ocupado ? 'Enviando…' : 'Enviar áudio'}
          </button>
          <button type="button" className="btn-texto" onClick={descartar} disabled={ocupado}>
            Descartar
          </button>
        </div>
      )}
    </div>
  );
}
